import { isEqual } from 'lodash';
import { differenceInMilliseconds, hoursToMilliseconds } from 'date-fns';

/**
 * Compares form previous and current order data and adds necessary updates:
 */
export function compareFormData(currentData, previousData) {
  // If nothing to compare, return the current data:
  if (!previousData) {
    return currentData;
  }
  const {
    offer: oldOffer,
    details: oldDetails,
    confirmationMailSentAt,
    staffingMailSentAt,
  } = previousData || {};
  const { shifts: oldShifts } = oldDetails || {};
  const { offer, details } = currentData || {};
  const { shifts } = details || {};
  const isConfirmationSent = !!confirmationMailSentAt;
  const isStaffingSent = !!staffingMailSentAt;
  // Has the offer items changed?
  const offerHasChanged = !isEqual(oldOffer, offer);
  // Has any nanny data been changed?
  let nannyDataHasChanged = false;
  shifts.forEach((shift, index) => {
    const { assignedTo: newNannies } = shift || {};
    const { assignedTo: oldNannies } = oldShifts[index] || {};
    if (!isEqual(oldNannies, newNannies)) {
      nannyDataHasChanged = true;
    }
  });
  return {
    ...currentData,
    ...(isConfirmationSent && offerHasChanged
      ? { shouldResendOfferMail: true }
      : {}),
    ...(isStaffingSent && nannyDataHasChanged
      ? { shouldResendStaffingMail: true }
      : {}),
  };
}

/**
 * Checks if shift dates are correct
 */
export function checkDates(value, index, allValues) {
  const { details } = allValues;
  const { shifts } = details;

  if (!value || (index !== 0 && !index)) {
    return 'Date is required';
  }
  const shift = shifts[index];
  const { start, end } = shift;
  const now = new Date();
  if (start && new Date(start) <= now) {
    return 'Start must be later than now.';
  }
  if (end && new Date(end) <= now) {
    return 'End must be later than now.';
  }
  if (start && end && new Date(start) >= new Date(end)) {
    return 'Start must be earlier than end.';
  }
  if (start && end && new Date(end) <= new Date(start)) {
    return 'End must be later than start.';
  }
  if (
    start &&
    end &&
    differenceInMilliseconds(new Date(end), new Date(start)) >
      hoursToMilliseconds(24)
  ) {
    return 'Shift duration cannot exceed 24 hours.';
  }
  return undefined;
}

/**
 * Checks if all shifts along with their sessions are approved.
 */
export function checkShiftsApproval(shifts) {
  const shiftApprovals = shifts.map(shift => {
    const { sessionApprovals } = shift;
    if (!sessionApprovals) {
      return false;
    }
    const numberOfNannies = shift.numberOfNannies || shift.assignedTo?.length || 1;
    const approvedShiftSessions = new Set(
      sessionApprovals
        .filter(s => s.isApproved && s.type === 'shift')
        .map(s => s.sessionID),
    );
    const approvedTravelSessions = new Set(
      sessionApprovals
        .filter(s => s.isApproved && s.type === 'travel')
        .map(s => s.sessionID),
    );
    if (shift.includesTravelTime) {
      return (
        approvedShiftSessions.size >= numberOfNannies &&
        approvedTravelSessions.size >= numberOfNannies * 2
      );
    }
    return approvedShiftSessions.size >= numberOfNannies;
  });
  return shiftApprovals.every(Boolean);
}
